import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';

// ============================================
// TYPES
// ============================================

export interface OtherUser {
  id: string;
  displayName: string;
  photos: { url: string; order: number }[] | null;
}

export interface Conversation {
  id: string;
  matchId: string;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  unreadCount: number;
  otherUser: OtherUser | null;
}

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  content: string;
  status: 'sent' | 'delivered' | 'read' | 'failed';
  createdAt: string;
  isAiSuggested: boolean;
  aiSuggestionTone?: string;
}

export interface Pagination {
  cursor: string | null;
  hasMore: boolean;
}

// ============================================
// API FUNCTIONS
// ============================================

async function fetchWithAuth(url: string, token: string, options: RequestInit = {}) {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }

  return response.json();
}

// ============================================
// HOOKS
// ============================================

// Fetch all conversations
export function useConversations() {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: ['conversations'],
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');

      const data = await fetchWithAuth(`${API_BASE}/api/messages/conversations`, token);
      return data.conversations as Conversation[];
    },
    refetchInterval: 10000, // Refresh every 10 seconds for unread counts
  });
}

// Get or create conversation for a match
export function useConversation(matchId: string | undefined) {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: ['conversation', matchId],
    queryFn: async () => {
      if (!matchId) throw new Error('Match ID required');

      const token = await getToken();
      if (!token) throw new Error('Not authenticated');

      const data = await fetchWithAuth(
        `${API_BASE}/api/messages/conversations/${matchId}`,
        token
      );
      return data.conversation as Conversation & { otherUser: OtherUser };
    },
    enabled: !!matchId,
  });
}

// Fetch messages for a conversation
export function useMessages(conversationId: string | undefined) {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: ['messages', conversationId],
    queryFn: async () => {
      if (!conversationId) throw new Error('Conversation ID required');

      const token = await getToken();
      if (!token) throw new Error('Not authenticated');

      const data = await fetchWithAuth(
        `${API_BASE}/api/messages/conversations/${conversationId}/messages`,
        token
      );
      return {
        messages: data.messages as Message[],
        pagination: data.pagination as Pagination,
      };
    },
    enabled: !!conversationId,
    refetchInterval: 3000, // Poll every 3 seconds for new messages
  });
}

// Send a message
export function useSendMessage() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      content,
      currentUserId: _currentUserId,
      isAiSuggested,
      suggestionTone,
    }: {
      conversationId: string;
      content: string;
      currentUserId?: string;
      isAiSuggested?: boolean;
      suggestionTone?: string;
    }) => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');

      const data = await fetchWithAuth(
        `${API_BASE}/api/messages/conversations/${conversationId}/messages`,
        token,
        {
          method: 'POST',
          body: JSON.stringify({ content, isAiSuggested, suggestionTone }),
        }
      );
      return data.message as Message;
    },
    onMutate: async ({ conversationId, content, currentUserId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['messages', conversationId] });

      // Snapshot previous value
      const previousMessages = queryClient.getQueryData(['messages', conversationId]);

      // Optimistically add the message
      queryClient.setQueryData(
        ['messages', conversationId],
        (old: { messages: Message[]; pagination: Pagination } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            messages: [
              ...old.messages,
              {
                id: `temp-${Date.now()}`,
                conversationId,
                senderId: currentUserId || 'me',
                content,
                status: 'sent' as const,
                createdAt: new Date().toISOString(),
                isAiSuggested: false,
              },
            ],
          };
        }
      );

      return { previousMessages };
    },
    onError: (_err, { conversationId }, context) => {
      // Rollback on error
      if (context?.previousMessages) {
        queryClient.setQueryData(['messages', conversationId], context.previousMessages);
      }
    },
    onSettled: (_data, _error, { conversationId }) => {
      // Refetch to sync with server
      queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

// Mark messages as read
export function useMarkRead() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ messageIds }: { messageIds: string[] }) => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');

      await fetchWithAuth(`${API_BASE}/api/messages/mark-read/read`, token, {
        method: 'PATCH',
        body: JSON.stringify({ messageIds }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

// Update typing status
export function useTypingIndicator(conversationId: string | undefined) {
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async (isTyping: boolean) => {
      if (!conversationId) return;

      const token = await getToken();
      if (!token) return;

      await fetchWithAuth(
        `${API_BASE}/api/messages/conversations/${conversationId}/typing`,
        token,
        {
          method: 'POST',
          body: JSON.stringify({ isTyping }),
        }
      );
    },
  });
}

// Get typing status
export function useTypingStatus(conversationId: string | undefined) {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: ['typing', conversationId],
    queryFn: async () => {
      if (!conversationId) return { isTyping: false };

      const token = await getToken();
      if (!token) return { isTyping: false };

      const data = await fetchWithAuth(
        `${API_BASE}/api/messages/conversations/${conversationId}/typing`,
        token
      );
      return data as { isTyping: boolean };
    },
    enabled: !!conversationId,
    refetchInterval: 2000, // Check every 2 seconds
  });
}

// Block user
export function useBlockUser() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, reason }: { userId: string; reason?: string }) => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');

      await fetchWithAuth(`${API_BASE}/api/messages/users/${userId}/block`, token, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['matches'] });
    },
  });
}

// Report user/message
export function useReport() {
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async (report: {
      reportedId: string;
      conversationId?: string;
      messageId?: string;
      category: string;
      description?: string;
    }) => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');

      const data = await fetchWithAuth(`${API_BASE}/api/messages/reports`, token, {
        method: 'POST',
        body: JSON.stringify(report),
      });
      return data.report;
    },
  });
}

